import { roomData } from './data';
import { IMatrixRoomInfo, IMatrixMessage } from './types';
import { messageUtils } from '../message/messageUtils';
import { memberUtils } from './memberUtils';
import { Log } from '@fanfanlo';
import { matrixClient } from '../client';

const fileLogger = new Log(false, 'im.client.room.roomService');
// fileLogger.childrenPaused = true;
// fileLogger.pause = true;
export const roomService = {
    // 获取已加入房间的ID集合
    getJoinedRoomIds() {
        return roomData.joinedRoomIds;
    },

    // 获取已加入的房间列表
    getJoinedRooms() {
        return roomData.joinedRooms;
    },

    // 添加房间到已加入列表
    addJoinedRoom(roomId: string) {
        roomData.joinedRoomIds.add(roomId);
        // 加载房间成员
        this.loadRoomMembers(roomId);
    },

    // 从已加入列表中移除房间
    removeJoinedRoom(roomId: string) {
        roomData.joinedRoomIds.delete(roomId);
        const index = roomData.joinedRooms.findIndex((r: IMatrixRoomInfo) => r.roomId === roomId);
        if (index !== -1) {
            roomData.joinedRooms.splice(index, 1);
            delete roomData.joinedRoomsMap[roomId];
        }
    },

    // 查找房间信息
    findRoomInfo(roomId: string): IMatrixRoomInfo | undefined {
        return roomData.joinedRooms.find(r => r.roomId === roomId);
    },

    // 添加或更新房间信息
    addOrUpdateRoomInfo(roomInfo: IMatrixRoomInfo) {
        const logger = fileLogger.sub(false, 'addOrUpdateRoomInfo');
        logger.pause = true;
        logger.log('roomInfo=', roomInfo);
        const index = roomData.joinedRooms.findIndex(r => r.roomId === roomInfo.roomId);
        if (index === -1) {
            roomData.joinedRooms.push(roomInfo);
            roomData.joinedRoomsMap[roomInfo.roomId] = roomInfo;
        } else {
            Object.assign(roomData.joinedRooms[index], roomInfo);
            // roomData.joinedRoomsMap[roomInfo.roomId] = roomInfo;
        }
        logger.log('roomData.joinedRoomsMap[roomInfo.roomId]=', roomData.joinedRoomsMap[roomInfo.roomId]);
        return roomInfo;
    },

    /**
     * 加载并保存历史消息
     * @param roomId 房间ID
     * @param fromEventId 从哪个事件ID开始加载更早的消息，如果不传则从最新消息开始加载
     * @param limit 每次加载的消息数量，默认20条
     * @returns 返回添加的消息ID列表和是否还有更多消息可以加载
     */
    async loadAndSaveMessages(roomId: string, fromEventId?: string, limit: number = 20): Promise<{messageIds: string[]; hasMore: boolean}> {
        const logger = fileLogger.sub(false, 'loadAndSaveMessages');
        logger.log(`Loading and saving messages for room ${roomId} from event ${fromEventId}, limit: ${limit}`);
        
        try {
            // 1. 加载消息
            const { messages, hasMore } = await this.loadMoreMessages(roomId, fromEventId, limit);
            
            if (messages.length === 0) {
                logger.log('No messages to save');
                return { messageIds: [], hasMore };
            }
            
            // 2. 批量添加消息到消息存储
            const messageIds = messageUtils.batchAddHistoryMessages(roomId, messages);
            
            logger.log(`Successfully saved ${messageIds.length} messages`, {
                roomId,
                firstMessageId: messageIds[0],
                lastMessageId: messageIds[messageIds.length - 1],
                hasMore
            });
            
            return { messageIds, hasMore };
            
        } catch (error) {
            logger.error('Failed to load and save messages:', error);
            throw error;
        }
    },
    
    /**
     * 加载更多历史消息
     * @param roomId 房间ID
     * @param fromEventId 从哪个事件ID开始加载更早的消息，如果不传则从最新消息开始加载
     * @param limit 每次加载的消息数量，默认20条
     * @returns 返回加载的消息列表和是否还有更多消息可以加载
     */
    async loadMoreMessages(roomId: string, fromEventId?: string, limit: number = 20): Promise<{messages: IMatrixMessage[]; hasMore: boolean}> {
        const logger = fileLogger.sub(false, 'loadMoreMessages');
        logger.log(`Loading more messages for room ${roomId} from event ${fromEventId}, limit: ${limit}`);
        
        try {
            const room = matrixClient.client.getRoom(roomId);
            if (!room) {
                logger.log('client getRoom Room not found:', roomId);
                return { messages: [], hasMore: false };
            }

            // 获取当前房间的消息ID列表
            const messageIds = room.getLiveTimeline().getEvents().map(event => event.getId()!);
            let startIndex = 0;
            
            // 如果提供了fromEventId，则找到该消息在列表中的位置
            if (fromEventId) {
                startIndex = messageIds.indexOf(fromEventId);
                if (startIndex === -1) {
                    // 如果没找到，则从最新消息开始加载
                    startIndex = 0;
                } else {
                    // 从找到的消息开始加载（不包含该消息）
                    startIndex++;
                }
            }
            
            // 计算结束位置
            const endIndex = Math.min(startIndex + limit, messageIds.length);
            
            // 获取消息ID范围
            const targetMessageIds = messageIds.slice(startIndex, endIndex);
            logger.log(`Loading messages from index ${startIndex} to ${endIndex}`, targetMessageIds);
            
            // 获取消息详情
            const events = await Promise.all(
                targetMessageIds.map(id => room.findEventById(id))
            );
            
            // 过滤掉未找到的消息
            const validEvents = events.filter(Boolean) as any[];
            
            // 转换为IMatrixMessage格式
            const messages = validEvents.map(event => ({
                event_id: event.getId(),
                sender: event.getSender(),
                origin_server_ts: event.getTs(),
                type: event.getType(),
                room_id: roomId,
                content: event.getContent()
            }));
            
            // 判断是否还有更多消息可以加载
            const hasMore = endIndex < messageIds.length;
            
            logger.log(`Loaded ${messages.length} messages, has more: ${hasMore}`);
            return { messages, hasMore };
            
        } catch (error) {
            logger.error('Failed to load more messages:', error);
            return { messages: [], hasMore: false };
        }
    },

    async sendTextMessage(roomId: string, text: string) {
        try {
            const response = await matrixClient.client.sendTextMessage(roomId, text);
            return response.event_id;
        } catch (error) {
            console.error('Failed to send message:', error);
            throw error;
        }
    },

    // 发送位置消息
    async sendLocationMessage(
        roomId: string,
        latitude: number,
        longitude: number,
        title: string = '',
        description: string = ''
    ) {
        try {
            const content = {
                msgtype: 'm.location',
                body: title || `位置: ${latitude}, ${longitude}`,
                geo_uri: `geo:${latitude},${longitude}`,
                info: {
                    title,
                    description
                }
            };
            
            const response = await matrixClient.client.sendMessage(roomId, content);
            return response.event_id;
        } catch (error) {
            console.error('Failed to send location message:', error);
            throw error;
        }
    },
    
    // 处理接收到的位置消息
    handleLocationMessage(roomId: string, event: any) {
        const message: IMatrixMessage = {
            event_id: event.event_id,
            type: 'm.location',
            sender: event.sender,
            origin_server_ts: event.origin_server_ts,
            room_id: roomId,
            content: {
                msgtype: 'm.location',
                body: event.content.body,
                geo_uri: event.content.geo_uri,
                info: event.content.info
            },
        };
        
        messageUtils.addMessage(roomId, message);
        return message;
    },

    // 成员相关方法
    async loadRoomMembers(roomId: string) {
        const logger = fileLogger.sub(false, 'loadRoomMembers_fn');
        logger.pause = true;
        try {
            const room = await matrixClient.client.getRoom(roomId);
            logger.log('room=', room)
            if (!room) {
                logger.log('loadRoomMembers Room not found:', roomId);
                return [];
            }
            const members = room.getJoinedMembers();
            logger.log('members=', members)
            const memberList = members.map((member: any) => ({
                userId: member.userId,
                displayName: member.rawDisplayName || member.name,
                avatarUrl: member.getMxcAvatarUrl(),
                membership: 'join' as const
            }));
            logger.log('memberList=', memberList)
            memberUtils.setRoomMembers(roomId, memberList);
            return memberList;
        } catch (error) {
            console.error('Failed to load room members:', error);
            return [];
        }
    },
    
    getRoomMembers(roomId: string) {
        return memberUtils.getRoomMembers(roomId);
    }
};
